import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import {
  useSearchFiltersByName,
  useSearchLocation,
  useSearchQuery,
  useSearchRadius,
} from '#/modules/search/contexts/SearchContext';
import { serializeActionDate } from '#/modules/search/helpers/actionDateSerializer';
import { radiusStringToInt } from '#/modules/search/helpers/radius';
import { filterToIndex } from '#/modules/search/helpers/savedSearches';
import { searchAddHasGeoToSearchLocation } from '#/modules/search/helpers/searchAddHasGeoToSearchLocation';
import type { ApiSavedSearchUnsaved } from '#/modules/userDashboard/api/savedSearch/types/UserDashboardApiSavedSearch';
import { useSavedSearchesActions } from '#/modules/userDashboard/contexts/SavedSearchesContext';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypePluralHumanNameMap';
import { getUserTimezone } from '#/utils/timezones';
import { deferWithLoadingAnimation } from '#/utils/ui/routeLoadingAnimation';
import { showModal } from '#/zustand-stores/modalStore';

export function useSaveSearch() {
  const filters = useSearchFiltersByName();
  const query = useSearchQuery();
  const searchLocation = useSearchLocation();
  const radius = useSearchRadius();
  const { i18n } = useI18n();
  const { createSavedSearch } = useSavedSearchesActions();

  const { user } = useUser();

  const showEmailSignupModal = (savedSearch: ApiSavedSearchUnsaved) => {
    const locationText = searchLocation?.text
      ? i18n('in %(locationText)s', {
          locationText: searchLocation.text,
        })
      : i18n('on Idealist');
    const listingTypeText =
      LISTING_TYPE_PLURAL_HUMAN_NAME_MAP(i18n)[filters.type];

    showModal('EMAIL_SIGNUP', {
      title: i18n(
        'Get email alerts about %(listingTypeText)s %(locationText)s',
        {
          listingTypeText,
          locationText,
        },
      ),
      tmpStorage: { type: 'savedSearch', savedSearch },
    });
  };

  const saveSearch = () => {
    const timezone = searchLocation?.timezone ?? getUserTimezone();

    let filtersAsTimestamps: Record<string, number | undefined> = {
      endsGT: undefined,
      startsLT: undefined,
    };

    if ('startsLT' in filters || 'endsGT' in filters) {
      filtersAsTimestamps = serializeActionDate({
        timezone,
        endsGT: filters.endsGT ?? null,
        startsLT: filters.startsLT ?? null,
      });
    }

    const search = {
      text: query,
      filters: {
        ...filters,
        endsGT: filtersAsTimestamps.endsGT,
        startsLT: filtersAsTimestamps.startsLT,
      },

      radius: radiusStringToInt(radius),
      location: searchLocation
        ? searchAddHasGeoToSearchLocation(searchLocation)
        : {},

      index: filterToIndex(filters.type),
    };

    const loggedInVerified = Boolean(
      user?.email !== null && user?.emailVerified,
    );
    const informationRequired =
      user && (!user.firstName || !user.lastName || !user.email);

    if (loggedInVerified && !informationRequired) {
      return createSavedSearch(search);
    }

    if (!loggedInVerified) {
      showEmailSignupModal(search);
    }
  };

  return {
    saveSearch: () => deferWithLoadingAnimation(saveSearch),
  };
}
